import { zodResolver } from "@hookform/resolvers/zod";
import isEqual from "lodash/isEqual";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState, type FC, type Reducer } from "react";
import { FormProvider, useFieldArray, useForm, type ErrorOption, type FieldErrors } from "react-hook-form";
import invariant from "tiny-invariant";
import { useMediaQuery } from "usehooks-ts";
import { z } from "zod";

import { useNavigate } from "react-router-dom";
import { useAccessControl } from "../../app/access-control-context";
import { ContentPad, ContentViewport, Leftside, Rightside, Utility } from "../../app/app-layout";
import { dispatchSidebarMenuTriggerEvent } from "../../app/app-menu";
import { Confirm, ConfirmHandle } from "../../components/confirm/confirm";
import { InquiryFormLeftPane, inquirySchema } from "../../components/inquiry-form/inquiry-form-left-pane";
import {
  InquiryFormRightPane,
  type InquiryFormRightPaneModes
} from "../../components/inquiry-form/inquiry-form-right-pane";
import { findPairRelationshipId } from "../../components/inquiry-form/relationship-utils";
import { getSelfEligibilityMessage, isSameContact } from "../../components/inquiry-form/self-state";
import { useModal } from "../../components/modal/modal";
import { NavTab, TabContainer, TabContent } from "../../components/nav-tabs/nav-tabs";
import { useCenterConstants } from "../../hooks/use-center";
import {
  getCenterContactFamilies,
  useFamilyConstants,
  useFamilyContactDetail,
  useFamilySelection,
  getStudentInquirerRelationshipId,
  type Family,
  type FamilyContact,
  type FamilyContactDetailWithId
} from "../../hooks/use-families";
import { defaultInquiryFormData, useCreateOrUpdateInquiry, type InquiryFormData } from "../../hooks/use-inquiries";
import { useAccessToken } from "../../hooks/use-access-token";
import { useNavigationConfirmation } from "../../hooks/use-navigation-confirmation";

const tabSchema = z.object({
  tabId: z.string()
});

const allInquiriesSchema = z.object({
  inquiries: z.array(tabSchema.and(inquirySchema))
});

type AllInquiriesType = z.infer<typeof allInquiriesSchema>;

const isFamilyContactDetail = (
  contact: FamilyContact | FamilyContactDetailWithId
): contact is FamilyContactDetailWithId => {
  return typeof (contact as FamilyContactDetailWithId).lastName === "string";
};

export const getNewFamilyName = (formData: InquiryFormData) => {
  const student = formData.student as InquiryFormData["student"] & { lastName?: string };
  const explicitLastName = student?.lastName?.trim() ?? "";

  if (!explicitLastName.length) {
    throw new Error("Student last name is required to derive family name");
  }

  return `${explicitLastName} Family`;
};

export const InquiryFormScreen: FC = () => {
  const confirmCloseWithoutSavingModalMultipleTabs = useRef<ConfirmHandle>(null);
  const confirmCloseWithoutSavingModalSingleTab = useModal({ disableToast: true });

  const getDefaultValues = useDefaultValuesFactory();

  const initialTabId = useMemo(() => crypto.randomUUID(), []);
  const [tabState, dispatch] = useReducer(tabReducer, {
    activeTabId: initialTabId,
    tabs: [
      {
        id: initialTabId,
        formValues: useMemo(() => getDefaultValues(), []),
        cleanFormValues: useMemo(() => getDefaultValues(), []),
        isDirty: false
      }
    ]
  });

  const { activeCenterId } = useAccessControl();
  const {
    activeConstants: { services }
  } = useCenterConstants(activeCenterId);
  const { getToken } = useAccessToken();

  const activeTab = useMemo(() => tabState.tabs.find(tab => tab.id === tabState.activeTabId), [tabState.activeTabId]);
  const activeTabIndex = useMemo(
    () => tabState.tabs.findIndex(tab => tab.id === tabState.activeTabId),
    [tabState.activeTabId]
  );
  invariant(activeTab, `Tab with ID "${tabState.activeTabId}" not found in tabs.`);

  const inquiryForm = useForm({
    values: activeTab.cleanFormValues,
    mode: "onTouched",
    // reValidateMode: "onChange",
    resolver: zodResolver(inquirySchema)
  });

  const allInquiries = useForm({
    values: { inquiries: tabState.tabs.map(tab => ({ tabId: tab.id, ...tab.formValues })) },
    resolver: zodResolver(allInquiriesSchema),
    resetOptions: {
      keepErrors: true
    }
  });

  const createOrUpdateInquiryMutator = useCreateOrUpdateInquiry();

  const onSaveInquiry = (savedInquiry: InquiryFormData) => {
    const savedFormValues = {
      ...inquiryForm.getValues(),
      id: savedInquiry.id,
      family: savedInquiry.family,
      familyId: savedInquiry.family?.id
    };

    window.toasts.add({ type: "success", content: "Saved inquiry successfully!" });
    inquiryForm.reset(savedFormValues);

    dispatch({
      type: "save_active_tab",
      payload: { savedInquiry: savedFormValues }
    });
  };

  const inquiryTabs = useFieldArray({ control: allInquiries.control, name: "inquiries" as never });

  useEffect(() => {
    // reset form when active tab changes
    inquiryForm.reset(activeTab.cleanFormValues);
    inquiryForm.reset(activeTab.formValues, { keepDefaultValues: true });
  }, [inquiryForm.reset, inquiryForm.setError, activeTab]);

  useEffect(() => {
    // set errors when active tab changes
    Object.entries(activeTab.validationErrors ?? {}).forEach(([field, error]) => {
      inquiryForm.setError(field as keyof InquiryFormData, error as ErrorOption);
    });
  }, [activeTab]);

  useEffect(() => {
    dispatch({
      type: "set_active_tab_validation_errors",
      payload: {
        errors: inquiryForm.formState.errors
      }
    });
  }, [inquiryForm.formState.errors]);

  useEffect(() => {
    dispatch({ type: "set_active_tab_dirty", payload: { isDirty: inquiryForm.formState.isDirty } });
  }, [inquiryForm.formState.isDirty]);

  useEffect(() => {
    dispatch({
      type: "set_all_validation_errors",
      payload: {
        errors: allInquiries.formState.errors
      }
    });
    Object.entries(allInquiries.formState.errors.inquiries?.[activeTabIndex] ?? {}).forEach(([field, error]) => {
      inquiryForm.setError(field as keyof InquiryFormData, error as ErrorOption);
    });
  }, [JSON.stringify(allInquiries.formState.errors)]);

  const isSmallScreen = useMediaQuery("(max-width: 991.98px)", { defaultValue: false });

  const [rightPaneMode, setRightPaneMode] = useState<InquiryFormRightPaneModes | undefined>(
    isSmallScreen ? undefined : "default"
  );
  useEffect(() => {
    if (!isSmallScreen && rightPaneMode === undefined) {
      setRightPaneMode("default");
    }
  }, [isSmallScreen, rightPaneMode]);

  const showDefaultRightPane = () => {
    setRightPaneMode("default");
  };

  const handleRightPaneClose = () => {
    setRightPaneMode(undefined);
  };

  const handleShowDetails = () => {
    setRightPaneMode("default");
  };

  const handleShowStudent = () => {
    setRightPaneMode("quick-create-student");
  };

  const handleShowInquirer = () => {
    setRightPaneMode("quick-create-inquirer");
  };

  const applyFamilySelection = useCallback(
    (selectedFamily: Family, options: { preserveSelectedContactType?: "inquirer" | "student" } = {}) => {
      const currentFamilyId = inquiryForm.getValues("family")?.id;
      const familyValue = { ...selectedFamily, name: selectedFamily.name ?? "" };
      const shouldResetSelectedContacts = currentFamilyId !== familyValue.id;
      const defaultValues = getDefaultValues();
      const shouldResetInquirer = options.preserveSelectedContactType !== "inquirer";
      const shouldResetStudent = options.preserveSelectedContactType !== "student";

      inquiryForm.setValue("family", familyValue, { shouldDirty: true, shouldValidate: true });
      inquiryForm.setValue("familyId", familyValue.id, { shouldDirty: true, shouldValidate: true });
      allInquiries.setValue(`inquiries.${activeTabIndex}.family`, familyValue, {
        shouldDirty: true,
        shouldValidate: true
      });
      allInquiries.setValue(`inquiries.${activeTabIndex}.familyId`, familyValue.id, {
        shouldDirty: true,
        shouldValidate: true
      });

      if (shouldResetSelectedContacts) {
        if (shouldResetInquirer) {
          inquiryForm.setValue("inquirer", defaultValues.inquirer, { shouldDirty: true, shouldValidate: true });
          allInquiries.setValue(`inquiries.${activeTabIndex}.inquirer`, defaultValues.inquirer, {
            shouldDirty: true,
            shouldValidate: true
          });
        }

        if (shouldResetStudent) {
          inquiryForm.setValue("student", defaultValues.student, { shouldDirty: true, shouldValidate: true });
          allInquiries.setValue(`inquiries.${activeTabIndex}.student`, defaultValues.student, {
            shouldDirty: true,
            shouldValidate: true
          });
        }

        const currentRelationshipId = inquiryForm.getValues("relationshipId") ?? "";
        if (currentRelationshipId === "") {
          inquiryForm.setValue("relationshipId", "", { shouldDirty: true, shouldValidate: true });
          allInquiries.setValue(`inquiries.${activeTabIndex}.relationshipId`, "", {
            shouldDirty: true,
            shouldValidate: true
          });
        }
      }

      showDefaultRightPane();
    },
    [inquiryForm, allInquiries, activeTabIndex, getDefaultValues, showDefaultRightPane]
  );

  const preloadFamilyFromContact = useCallback(
    async (type: "inquirer" | "student", contact: FamilyContact) => {
      try {
        const response = await getCenterContactFamilies(await getToken(), activeCenterId, {
          contact_id: contact.id
        });

        const selectedFamily = response.families.find(family => !!family.id);
        if (!selectedFamily?.id) {
          return;
        }

        applyFamilySelection(
          { id: selectedFamily.id, name: selectedFamily.name ?? "" },
          { preserveSelectedContactType: type }
        );
      } catch {
        // Best-effort preload. Leave current selections untouched on lookup failure.
      }
    },
    [activeCenterId, getToken, applyFamilySelection]
  );

  const handleSelectContact = useCallback(
    (type: "inquirer" | "student", setValue = true) =>
      (contact: FamilyContact | FamilyContactDetailWithId) => {
        if (setValue) {
          inquiryForm.setValue(type, contact, { shouldDirty: true, shouldValidate: true });
          allInquiries.setValue(`inquiries.${activeTabIndex}.${type}`, contact, {
            shouldDirty: true,
            shouldValidate: true
          });

          const selectedFamily = inquiryForm.getValues("family");
          if (type === "student" && selectedFamily?.id === "" && isFamilyContactDetail(contact)) {
            const trimmedLastName = contact.lastName.trim();
            if (trimmedLastName.length > 0) {
              const renamedFamily = {
                ...selectedFamily,
                name: `${trimmedLastName} Family`
              };

              inquiryForm.setValue("family", renamedFamily, { shouldDirty: true, shouldValidate: true });
              allInquiries.setValue(`inquiries.${activeTabIndex}.family`, renamedFamily, {
                shouldDirty: true,
                shouldValidate: true
              });
            }
          }

          void preloadFamilyFromContact(type, contact);
        }
      },
    [inquiryForm.setValue, allInquiries.setValue, activeTabIndex, preloadFamilyFromContact]
  );

  const { EMERGENCY_CONTACT_VALUE, activeRelationships, relationships } = useFamilyConstants();
  const setRelationshipValue = useCallback(
    (
      nextRelationshipId: string,
      options: {
        shouldDirty: boolean;
        shouldValidate: boolean;
        shouldTouch?: boolean;
      }
    ) => {
      inquiryForm.setValue("relationshipId", nextRelationshipId, options);
      allInquiries.setValue(`inquiries.${activeTabIndex}.relationshipId`, nextRelationshipId, options);
    },
    [inquiryForm, allInquiries, activeTabIndex]
  );

  const [relationshipId, inquirer, student, family, serviceId] = inquiryForm.watch([
    "relationshipId",
    "inquirer",
    "student",
    "family",
    "serviceId"
  ]);
  const loadedRelationshipId = `${activeTab.cleanFormValues.relationshipId ?? ""}`;
  const loadedInquirerId = `${activeTab.cleanFormValues.inquirer?.id ?? ""}`;
  const loadedStudentId = `${activeTab.cleanFormValues.student?.id ?? ""}`;
  const sameContact = isSameContact(inquirer, student);
  const hasSelectedInquirer = Boolean(inquirer?.id);
  const hasSelectedStudent = Boolean(student?.id);
  const differentSelectedContacts = hasSelectedInquirer && hasSelectedStudent && !sameContact;
  const selfStateChangedFromLoaded =
    `${relationshipId ?? ""}` !== loadedRelationshipId ||
    `${inquirer?.id ?? ""}` !== loadedInquirerId ||
    `${student?.id ?? ""}` !== loadedStudentId;
  const selfRelationshipId = getStudentInquirerRelationshipId(relationships) ?? "";
  const lockRelationshipToSelf =
    sameContact &&
    Boolean(selfRelationshipId) &&
    (selfStateChangedFromLoaded || `${relationshipId ?? ""}` === selfRelationshipId);
  const preserveLegacyDifferentContactSelf =
    differentSelectedContacts && `${relationshipId ?? ""}` === selfRelationshipId && !selfStateChangedFromLoaded;

  useEffect(() => {
    const nextRelationshipId = relationshipId ?? "";

    allInquiries.setValue(`inquiries.${activeTabIndex}.relationshipId`, nextRelationshipId, {
      shouldDirty: false,
      shouldValidate: nextRelationshipId !== "",
      shouldTouch: false
    });

    if (nextRelationshipId !== "") {
      inquiryForm.clearErrors("relationshipId");
    }
  }, [relationshipId, allInquiries, activeTabIndex, inquiryForm]);

  useEffect(() => {
    const nextRelationshipId = relationshipId ?? "";

    if (lockRelationshipToSelf) {
      if (selfRelationshipId && nextRelationshipId !== selfRelationshipId) {
        setRelationshipValue(selfRelationshipId, {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: false
        });
      }
      return;
    }

    if (
      selfStateChangedFromLoaded &&
      differentSelectedContacts &&
      selfRelationshipId &&
      nextRelationshipId === selfRelationshipId
    ) {
      setRelationshipValue("", {
        shouldDirty: true,
        shouldValidate: false,
        shouldTouch: false
      });
    }
  }, [
    lockRelationshipToSelf,
    selfStateChangedFromLoaded,
    differentSelectedContacts,
    relationshipId,
    selfRelationshipId,
    setRelationshipValue
  ]);

  const relationshipPairKey = inquirer?.id && student?.id ? `${inquirer.id}:${student.id}` : "";
  const selectableRelationshipIds = useMemo(
    () => activeRelationships.map(({ value }) => `${value}`),
    [activeRelationships]
  );
  const { data: familySelectionList } = useFamilySelection({ inquirerId: inquirer?.id, studentId: student?.id });
  const suggestedRelationshipId = useMemo(
    () =>
      findPairRelationshipId({
        familySelectionList,
        inquirerId: inquirer?.id,
        studentId: student?.id,
        emergencyContactRelationshipId: EMERGENCY_CONTACT_VALUE,
        selectableRelationshipIds
      }),
    [familySelectionList, inquirer?.id, student?.id, EMERGENCY_CONTACT_VALUE, selectableRelationshipIds]
  );
  const lastRelationshipPair = useRef<string>("");
  const pendingRelationshipPair = useRef<string>("");

  useEffect(() => {
    if (sameContact) {
      pendingRelationshipPair.current = "";
      lastRelationshipPair.current = relationshipPairKey;
      return;
    }

    const currentRelationshipId = inquiryForm.getValues("relationshipId") ?? "";
    const nextRelationshipId = suggestedRelationshipId ?? "";
    const pairChanged = relationshipPairKey !== lastRelationshipPair.current;
    if (pairChanged) {
      pendingRelationshipPair.current = relationshipPairKey;
    }

    const shouldAutoPopulateForNewPair =
      pendingRelationshipPair.current === relationshipPairKey &&
      relationshipPairKey !== "" &&
      nextRelationshipId !== "";
    const shouldAutoPopulateForCurrentPair = !pairChanged && currentRelationshipId === "" && nextRelationshipId !== "";

    if (shouldAutoPopulateForNewPair || shouldAutoPopulateForCurrentPair) {
      setRelationshipValue(nextRelationshipId, {
        shouldDirty: false,
        shouldValidate: true,
        shouldTouch: false
      });

      pendingRelationshipPair.current = "";
    }

    lastRelationshipPair.current = relationshipPairKey;
  }, [sameContact, suggestedRelationshipId, relationshipPairKey, inquiryForm, setRelationshipValue]);

  const { contactDetail: sameContactDetail } = useFamilyContactDetail("student", sameContact ? student?.id : undefined);
  const selfRelationshipValidationMessage = useMemo(
    () => (sameContact ? getSelfEligibilityMessage(sameContactDetail) : ""),
    [sameContact, sameContactDetail]
  );

  const handleSaveInquiry = (saveAction: SaveAction): React.MouseEventHandler<HTMLButtonElement> =>
    inquiryForm.handleSubmit(async formValues => {
      if (sameContact && selfStateChangedFromLoaded && selfRelationshipValidationMessage) {
        window.toasts.add({ content: selfRelationshipValidationMessage });
        return;
      }

      createOrUpdateInquiryMutator.mutate(
        { inquiryId: formValues.id, formData: formValues },
        {
          onSuccess: savedInquiry => {
            onSaveInquiry(savedInquiry);

            if (saveAction === "close") {
              setNavigationTo("/inquiry");
              return;
            }

            addInquiryTab(saveAction === "add-service");
          }
        }
      );
    });

  const handleCreateNewContact = (type: "inquirer" | "student") => {
    const selectedFamily = inquiryForm.getValues("family");
    const hasSelectedFamily = selectedFamily?.id !== undefined && selectedFamily?.id !== null;

    if (!hasSelectedFamily) {
      applyFamilySelection({
        id: "",
        name: "Create a New Family"
      });
    }

    const defaultFieldValue = getDefaultValues()[type];
    inquiryForm.setValue(type, defaultFieldValue, { shouldDirty: true, shouldValidate: true });
    allInquiries.setValue(`inquiries.${activeTabIndex}.${type}`, defaultFieldValue, {
      shouldDirty: true,
      shouldValidate: true
    });
    setRightPaneMode(type === "inquirer" ? "quick-create-inquirer" : "quick-create-student");
  };

  const handleSelectFamily = (family: Family) => {
    applyFamilySelection(family);
  };

  const handleResetFamily = useCallback(() => {
    const currentFamilyId = inquiryForm.getValues("family")?.id;
    const shouldResetSelectedContacts = currentFamilyId !== undefined && currentFamilyId !== null;
    const defaultValues = getDefaultValues();
    const resetFieldOptions = { shouldDirty: true, shouldValidate: false, shouldTouch: false } as const;

    inquiryForm.setValue("family", undefined, resetFieldOptions);
    inquiryForm.setValue("familyId", undefined, resetFieldOptions);
    allInquiries.setValue(`inquiries.${activeTabIndex}.family`, undefined, {
      shouldDirty: true,
      shouldValidate: false,
      shouldTouch: false
    });
    allInquiries.setValue(`inquiries.${activeTabIndex}.familyId`, undefined, {
      shouldDirty: true,
      shouldValidate: false,
      shouldTouch: false
    });

    inquiryForm.setValue("relationshipId", "", resetFieldOptions);
    allInquiries.setValue(`inquiries.${activeTabIndex}.relationshipId`, "", {
      shouldDirty: true,
      shouldValidate: false,
      shouldTouch: false
    });

    if (shouldResetSelectedContacts) {
      inquiryForm.setValue("inquirer", defaultValues.inquirer, resetFieldOptions);
      allInquiries.setValue(`inquiries.${activeTabIndex}.inquirer`, defaultValues.inquirer, {
        shouldDirty: true,
        shouldValidate: false,
        shouldTouch: false
      });

      inquiryForm.setValue("student", defaultValues.student, resetFieldOptions);
      allInquiries.setValue(`inquiries.${activeTabIndex}.student`, defaultValues.student, {
        shouldDirty: true,
        shouldValidate: false,
        shouldTouch: false
      });
    }

    inquiryForm.clearErrors(["family", "inquirer", "student", "relationshipId"]);
    allInquiries.clearErrors([
      `inquiries.${activeTabIndex}.family`,
      `inquiries.${activeTabIndex}.familyId`,
      `inquiries.${activeTabIndex}.inquirer`,
      `inquiries.${activeTabIndex}.student`,
      `inquiries.${activeTabIndex}.relationshipId`
    ]);

    showDefaultRightPane();
  }, [inquiryForm, allInquiries, activeTabIndex, getDefaultValues, showDefaultRightPane]);

  const leftPaneProps = {
    onSelectFamily: handleSelectFamily,
    onResetFamily: handleResetFamily,
    onSelectInquirer: handleSelectContact("inquirer"),
    onSelectStudent: handleSelectContact("student"),
    inquiryForm,
    onCreateNewContact: handleCreateNewContact,
    handleShowStudent,
    handleShowInquirer,
    sameContact,
    selfRelationshipId,
    lockRelationshipToSelf,
    preserveLegacyDifferentContactSelf
  };

  const rightPaneProps = {
    mode: rightPaneMode,
    onSelectContact: (type: "inquirer" | "student", contact: FamilyContact) => handleSelectContact(type)(contact),
    onSelectFamily: handleSelectFamily,
    onSaveInquiry: onSaveInquiry,
    onClose: handleRightPaneClose,
    student,
    inquirer,
    family
  };

  const getTabName = (student: FamilyContactDetailWithId | undefined, serviceId: string | "") => {
    const name = student?.displayName || `New Student`;

    const service = services.find(({ value }) => `${value}` === `${serviceId}`)?.name;
    const serviceSplit = service?.split(" ");
    const serviceAbbreviated =
      (serviceSplit?.length || 0) > 1
        ? serviceSplit?.reduce((response, word) => (response += word.slice(0, 1)), "")
        : service;

    return [name, serviceAbbreviated].filter(itm => itm !== undefined).join(" - ");
  };

  const shouldLoadStudentDetail = family?.id !== "";
  const { contactDetail: studentDetail } = useFamilyContactDetail(
    "student",
    shouldLoadStudentDetail ? student?.id : undefined
  );

  const nameGradeHeader = useMemo(() => {
    if (!student?.displayName) {
      return <strong>New Student</strong>;
    }
    if (studentDetail?.grade) {
      return (
        <>
          <strong>{student.displayName}</strong> ({studentDetail.grade.name})
        </>
      );
    }
    return <strong>{student.displayName}</strong>;
  }, [studentDetail]);

  const tabNameMapper = useMemo(() => {
    const results: Record<UUID, string> = {};
    tabState.tabs.forEach(tab => {
      results[tab.id] =
        tab.id === tabState.activeTabId
          ? getTabName(student as FamilyContactDetailWithId, serviceId)
          : getTabName(tab.formValues.student as FamilyContactDetailWithId, tab.formValues.serviceId);
    });

    return results;
  }, [tabState.tabs.length, student, serviceId]);

  const inquiriesDropdownOptions = useMemo(() => {
    return tabState.tabs.map(tab => {
      return (
        <option key={tab.id} value={tab.id} selected={tab.id === tabState.activeTabId}>
          {tabNameMapper[tab.id]}
        </option>
      );
    });
  }, [tabNameMapper]);

  const [navigateTo, setNavigationTo] = useState<string | null>(null);
  const { blocker } = useNavigationConfirmation({
    confirmNavigation: !!tabState.tabs.find(tab => tab.isDirty) && !navigateTo
  });

  useEffect(() => {
    if (blocker.state === "blocked") {
      dispatchSidebarMenuTriggerEvent(false);
      confirmCloseWithoutSavingModalSingleTab.show();
    }
  }, [blocker.state]);

  useEffect(() => {
    if (navigateTo) {
      navigate(navigateTo);
    }
  }, [navigateTo]);

  const addInquiryTab = (inheritFormValues: boolean) => {
    const newTabId = crypto.randomUUID();
    const cleanFormValues = getDefaultValues();
    const fromTabValues = inquiryForm.getValues();
    const newTabValues = inheritFormValues
      ? {
          ...cleanFormValues,
          inquirer: fromTabValues.inquirer,
          inquirerId: fromTabValues.inquirerId,
          family: fromTabValues.family,
          familyId: fromTabValues.familyId,
          relationshipId: fromTabValues.relationshipId,
          // sourceId: fromTabValues.sourceId,
          // referredById: fromTabValues.referredById,
          // referredByOther: fromTabValues.referredByOther,
          id: undefined
        }
      : cleanFormValues;
    dispatch({
      type: "add_tab",
      payload: {
        fromTabValues,
        newTabValues,
        cleanFormValues,
        newTabId
      }
    });
    inquiryTabs.append({ tabId: newTabId });
  };

  const navigate = useNavigate();

  const handleCancel = (event: React.MouseEvent) => {
    if (tabState.tabs.length === 1) {
      confirmCloseWithoutSavingModalSingleTab.show();
    } else {
      // if (activeTab.isDirty) {
      confirmCloseWithoutSavingModalMultipleTabs.current?.confirm(() => {
        dispatch({
          type: "remove_active_tab"
        });
        inquiryTabs.remove(activeTabIndex);
      });
      // } else {
      //   dispatch({
      //     type: "remove_active_tab"
      //   });
      //   inquiryTabs.remove(activeTabIndex);
      // }
      event.stopPropagation();
    }
  };

  const handleInquirySelectorDropdownChange: React.ChangeEventHandler<HTMLSelectElement> = event => {
    dispatch({
      type: "switch_tabs",
      payload: {
        fromTabValues: inquiryForm.getValues(),
        errors: inquiryForm.formState.errors,
        toTabId: event.target.value as UUID
      }
    });
  };

  const handleCloseConfirmSingleTabModal: React.MouseEventHandler = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    confirmCloseWithoutSavingModalSingleTab.hide();
  };

  const handleDiscardConfirmSingleTabModal: React.MouseEventHandler = () => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      setNavigationTo("/inquiry");
    }
  };

  return (
    <ContentViewport className="slide-in-sidebar">
      <FormProvider {...inquiryForm}>
        <Leftside className="fixed-utility" sizeClasses="col-12 col-lg-7">
          <div className="sticky-top">
            <Utility>
              <div className="d-flex  stack-sm-down w-100 justify-content-between">
                <div className="ms-auto d-flex">
                  <button className="btn btn-secondary me-2" onClick={event => handleCancel(event)}>
                    Cancel
                  </button>
                  <div className="dropdown">
                    <button
                      className="btn btn-vcondensed btn-primary border"
                      id="inquiries-save-dropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      Save
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="inquiries-save-dropdown">
                      <li>
                        <button
                          className="dropdown-item"
                          disabled={createOrUpdateInquiryMutator.isLoading}
                          onClick={handleSaveInquiry("close")}
                        >
                          Save &amp; Close
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          disabled={createOrUpdateInquiryMutator.isLoading}
                          onClick={handleSaveInquiry("add-service")}
                        >
                          Save &amp; Add Service
                        </button>
                      </li>
                      <li>
                        <button
                          className="dropdown-item"
                          disabled={createOrUpdateInquiryMutator.isLoading}
                          onClick={handleSaveInquiry("new-inquiry")}
                        >
                          Save &amp; New Inquiry
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Utility>
            <div className="post-utility-bar">
              <div>
                <h2>{nameGradeHeader}</h2>
              </div>
              <div>
                <a href="#inquiry-form-notes">Go to Notes</a>
                <span className="d-lg-none">
                  &nbsp;|
                  <button className="btn-link" onClick={handleShowDetails}>
                    View Details
                  </button>
                </span>
              </div>
            </div>
          </div>
          <ContentPad className="mh-100">
            {tabState.tabs.length > 1 && (
              <div className="mb-4 d-md-none">
                <div className="form-floating">
                  <select
                    className="form-select"
                    onChange={handleInquirySelectorDropdownChange}
                    id="inquiries-current-inquiry-selector"
                    aria-label="Inquiries"
                  >
                    {inquiriesDropdownOptions}
                  </select>
                  <label htmlFor="inquiries-current-inquiry-selector">Inquiries</label>
                </div>
              </div>
            )}
            <TabContainer className="nav-fill student-tabs d-none d-md-flex">
              {tabState.tabs.map(tab => (
                <NavTab
                  key={tab.id}
                  href={`#tab-${tab.id}`}
                  isActive={tab.id === tabState.activeTabId}
                  onClick={() => {
                    dispatch({
                      type: "switch_tabs",
                      payload: {
                        fromTabValues: inquiryForm.getValues(),
                        errors: inquiryForm.formState.errors,
                        toTabId: tab.id
                      }
                    });
                  }}
                  className="h-100"
                  style={{
                    color:
                      // allInquiries.formState.errors?.inquiries?.[index] ||
                      Object.entries(tab.validationErrors ?? {}).length ? "red" : undefined
                  }}
                >
                  <span className="fas icon fa-user" aria-hidden="true"></span>
                  {tabNameMapper[tab.id]}
                  {tab.isDirty && " *"}
                </NavTab>
              ))}
            </TabContainer>
            <TabContent>
              <div id="new-student" className="tab-pane card active">
                <InquiryFormLeftPane {...leftPaneProps} />
              </div>
            </TabContent>
          </ContentPad>
        </Leftside>
        <Rightside showIf={rightPaneMode !== undefined} sizeClasses="fixed-utility col-12 col-lg-5">
          <InquiryFormRightPane {...rightPaneProps} />
        </Rightside>
        {confirmCloseWithoutSavingModalSingleTab.renderModal({
          title: "Unsaved Changes",
          body: (
            <p>
              If you leave this page without saving, your inquiry will not be saved.
              <br />
              <br />
              Do you wish to leave and discard your changes, or stay on the page and continue editing?
            </p>
          ),
          footer: (
            <>
              <button autoFocus={true} className="btn btn-primary" onClick={handleCloseConfirmSingleTabModal}>
                Stay
              </button>
              <button className="btn btn-secondary" onClick={handleDiscardConfirmSingleTabModal}>
                Leave and Discard
              </button>
            </>
          )
        })}
        <Confirm
          ref={confirmCloseWithoutSavingModalMultipleTabs}
          title="Confirm"
          message="Would you like to close the Inquiry Form and lose any entered data?"
          okLabel="Yes"
          cancelLabel="No"
          superZ={true}
        />
      </FormProvider>
    </ContentViewport>
  );
};

type SaveAction = "close" | "add-service" | "new-inquiry";

type UUID = ReturnType<typeof crypto.randomUUID>;

type InquiryTabState = {
  activeTabId: UUID;
  tabs: Array<{
    id: UUID;
    formValues: InquiryFormData;
    cleanFormValues: InquiryFormData;
    isDirty: boolean;
    validationErrors?: FieldErrors<InquiryFormData>;
  }>;
};

type InquiryTabAction =
  | {
      type: "add_tab";
      payload: {
        newTabId: UUID;
        fromTabValues: InquiryFormData;
        newTabValues: InquiryFormData;
        cleanFormValues: InquiryFormData;
      };
    }
  | {
      type: "switch_tabs";
      payload: { fromTabValues: InquiryFormData; toTabId: UUID; errors: FieldErrors<InquiryFormData> };
    }
  | { type: "remove_active_tab" }
  | { type: "save_active_tab"; payload: { savedInquiry: InquiryFormData } }
  | { type: "patch_tab_form_values"; payload: { tabId: UUID; patch: Partial<InquiryFormData> } }
  | { type: "set_active_tab_dirty"; payload: { isDirty: boolean } }
  | { type: "set_active_tab_validation_errors"; payload: { errors: FieldErrors<InquiryFormData> } }
  | { type: "set_all_validation_errors"; payload: { errors: FieldErrors<AllInquiriesType> } };

const tabReducer: Reducer<InquiryTabState, InquiryTabAction> = (state, action) => {
  switch (action.type) {
    case "add_tab": {
      const { newTabId } = action.payload;
      const currentTab = state.tabs.find(({ id }) => id === state.activeTabId);
      invariant(currentTab, `Tab with ID "${state.activeTabId}" not found in tabs.`);
      const { cleanFormValues, fromTabValues, newTabValues } = action.payload;

      return {
        activeTabId: newTabId,
        tabs: [
          ...state.tabs.map(tab => (tab.id === state.activeTabId ? { ...tab, formValues: fromTabValues } : tab)),
          {
            id: newTabId,
            formValues: newTabValues,
            cleanFormValues,
            isDirty: !isEqual(newTabValues, cleanFormValues),
            validationErrors: currentTab.validationErrors
          }
        ]
      };
    }
    case "switch_tabs":
      const currentTab = state.tabs.find(({ id }) => id === state.activeTabId);
      invariant(currentTab, `Tab with ID "${state.activeTabId}" not found in tabs.`);
      return {
        activeTabId: action.payload.toTabId,
        tabs: state.tabs.map(tab =>
          tab.id === state.activeTabId ? { ...tab, formValues: action.payload.fromTabValues } : tab
        )
      };
    case "remove_active_tab":
      const index = state.tabs.findIndex(tab => tab.id === state.activeTabId);
      const newActiveTab = state.tabs[index + 1] ?? state.tabs[index - 1];
      return {
        activeTabId: newActiveTab.id,
        tabs: [...state.tabs.filter(tab => tab.id !== state.activeTabId)]
      };
    case "save_active_tab":
      const savedFormValues = action.payload.savedInquiry;
      return {
        activeTabId: state.activeTabId,
        tabs: state.tabs.map(tab =>
          tab.id === state.activeTabId
            ? {
                id: tab.id,
                formValues: savedFormValues,
                cleanFormValues: savedFormValues,
                // formValues: action.payload.savedInquiry,
                // cleanFormValues: action.payload.savedInquiry,
                isDirty: false
              }
            : tab
        )
      };
    case "patch_tab_form_values":
      return {
        activeTabId: state.activeTabId,
        tabs: state.tabs.map(tab =>
          tab.id === action.payload.tabId
            ? {
                ...tab,
                formValues: {
                  ...tab.formValues,
                  ...action.payload.patch
                }
              }
            : tab
        )
      };
    case "set_active_tab_dirty":
      return {
        activeTabId: state.activeTabId,
        tabs: state.tabs.map(tab => (tab.id === state.activeTabId ? { ...tab, isDirty: action.payload.isDirty } : tab))
      };
    case "set_active_tab_validation_errors":
      return {
        activeTabId: state.activeTabId,
        tabs: state.tabs.map(tab =>
          tab.id === state.activeTabId ? { ...tab, validationErrors: action.payload.errors } : tab
        )
      };
    case "set_all_validation_errors":
      return {
        activeTabId: state.activeTabId,
        tabs: state.tabs.map((tab, index) => ({
          ...tab,
          validationErrors: action.payload.errors.inquiries?.[index] as FieldErrors<AllInquiriesType>
        }))
      };
  }
};

const useDefaultValuesFactory = () => {
  const { profile, activeCenterId } = useAccessControl();
  const {
    activeConstants: { timezone }
  } = useCenterConstants(activeCenterId);

  return () => {
    const inquiryStart = moment(new Date()).tz(timezone);

    return {
      ...defaultInquiryFormData,
      inquiryDate: inquiryStart.format("YYYY-MM-DD"),
      inquiryTimeReceived: inquiryStart.format("HH:mm"),
      takenBy: profile
    };
  };
};

export const inquiryFormLoader = () => {
  return {
    activeTab: "Inquiry Management",
    tabQualifier: "Inquiry Form",
    dontSlideInSidebar: true
  };
};
